import React, { useEffect, useState, useRef } from 'react';
import { PaymentMethod } from './PaymentMethod';
import { ShopifySubscription } from './ShopifySubscription';
import { StripeUsageFees } from './StripeUsageFees';
import { PulseSubscription } from './PulseSubscription';
import { MoreVerticalIcon, X as XIcon } from 'lucide-react';
import { StatusBadge } from './SubscriptionStatus';
import { subscriptionService } from '../services/subscriptionService';
import type { Subscription } from '../types';
const CancellationModal = ({
  onClose,
  onConfirm,
  subscriptionName
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const modalRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  const handleConfirm = () => {
    if (!reason.trim()) {
      setError('Please provide a reason for cancellation');
      return;
    }
    onConfirm(reason);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-35">
      <div ref={modalRef} className="bg-white rounded-lg shadow-lg w-full max-w-md p-6" data-id="element-36">
        <div className="flex justify-between items-center mb-4" data-id="element-37">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-38">
            Cancel {subscriptionName} Subscription
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-39">
            <XIcon size={20} data-id="element-40" />
          </button>
        </div>
        <p className="text-gray-600 mb-4" data-id="element-41">
          Are you sure you want to cancel your subscription?
        </p>
        <div className="mb-4" data-id="element-42">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-43">
            Please tell us why you're cancelling
          </label>
          <textarea value={reason} onChange={e => {
          setReason(e.target.value);
          if (e.target.value.trim()) setError('');
        }} className={`w-full px-3 py-2 border rounded-md ${error ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`} rows={3} placeholder="Your feedback helps us improve our service..." data-id="element-44"></textarea>
          {error && <p className="mt-1 text-sm text-red-600" data-id="element-45">{error}</p>}
        </div>
        <div className="flex justify-end space-x-3" data-id="element-46">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-47">
            Cancel
          </button>
          <button onClick={handleConfirm} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-48">
            Confirm Cancellation
          </button>
        </div>
      </div>
    </div>;
};
const ActivationModal = ({
  onClose,
  onConfirm,
  subscriptionName
}) => {
  const modalRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-49">
      <div ref={modalRef} className="bg-white rounded-lg shadow-lg w-full max-w-md p-6" data-id="element-50">
        <div className="flex justify-between items-center mb-4" data-id="element-51">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-52">
            Activate {subscriptionName} Subscription
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-53">
            <XIcon size={20} data-id="element-54" />
          </button>
        </div>
        <p className="text-gray-600 mb-4" data-id="element-55">
          Are you sure you want to activate this subscription?
        </p>
        <div className="flex justify-end space-x-3" data-id="element-56">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-57">
            Cancel
          </button>
          <button onClick={onConfirm} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-58">
            Activate Subscription
          </button>
        </div>
      </div>
    </div>;
};
const SubscriptionMenu = ({
  onCancel,
  onActivate,
  status,
  onClose
}) => {
  const menuRef = useRef(null);
  const canCancel = ['active', 'trialing', 'incomplete_payment'].includes(status);
  const canActivate = ['cancelled', 'inactive'].includes(status);
  const showUpgrade = ['inactive', 'not_active', 'incomplete_payment', 'discounted', 'not_subscribed'].includes(status);
  useEffect(() => {
    const handleClickOutside = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  const handleUpgrade = () => {
    window.location.href = '/payment'; // Replace with actual payment page URL
    onClose();
  };
  return <div ref={menuRef} className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-59">
      <div className="py-1" data-id="element-60">
        {showUpgrade && <button onClick={handleUpgrade} className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-50" data-id="element-61">
            Upgrade
          </button>}
        {canCancel && <button onClick={onCancel} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50" data-id="element-62">
            Cancel Subscription
          </button>}
        {canActivate && <button onClick={onActivate} className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-50" data-id="element-63">
            Activate Subscription
          </button>}
      </div>
    </div>;
};
export const BillingPage = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blendMenuOpen, setBlendMenuOpen] = useState(false);
  const [pulseMenuOpen, setPulseMenuOpen] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  useEffect(() => {
    loadSubscriptions();
  }, []);
  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      // Replace 'your-account-id' with actual account ID from your auth context or environment
      const data = await subscriptionService.getSubscriptions('your-account-id');
      setSubscriptions(data);
      setError(null);
    } catch (err) {
      setError('Failed to load subscriptions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const handleCancelClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowCancelModal(true);
    setBlendMenuOpen(false);
    setPulseMenuOpen(false);
  };
  const handleActivateClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowActivateModal(true);
    setBlendMenuOpen(false);
    setPulseMenuOpen(false);
  };
  const handleCancelConfirm = async (reason: string) => {
    if (!selectedSubscription) return;
    try {
      // First, send the cancellation data to the webhook
      const webhookResponse = await fetch('https://n8n.blend-ai.com/webhook/aca41727-7cf0-4d71-838c-01faf97cb78f', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subscription_type: selectedSubscription.name,
          account_id: 838,
          feedback: reason
        })
      });
      if (!webhookResponse.ok) {
        throw new Error('Failed to send cancellation feedback');
      }
      // Then proceed with the subscription cancellation
      await subscriptionService.cancelSubscription(selectedSubscription.id, reason);
      await loadSubscriptions(); // Reload subscriptions after cancellation
    } catch (err) {
      console.error('Failed to process cancellation:', err);
      // You might want to show an error message to the user here
    } finally {
      setShowCancelModal(false);
      setSelectedSubscription(null);
    }
  };
  const handleActivateConfirm = async () => {
    if (!selectedSubscription) return;
    try {
      await subscriptionService.activateSubscription(selectedSubscription.id);
      await loadSubscriptions(); // Reload subscriptions after activation
    } catch (err) {
      console.error('Failed to activate subscription:', err);
    } finally {
      setShowActivateModal(false);
      setSelectedSubscription(null);
    }
  };
  const handleUpgrade = async (subscription: Subscription) => {
    try {
      await subscriptionService.upgradeSubscription(subscription.id);
      await loadSubscriptions(); // Reload subscriptions after upgrade
    } catch (err) {
      console.error('Failed to upgrade subscription:', err);
    }
  };
  if (loading) {
    return <div className="p-6" data-id="element-64">Loading...</div>;
  }
  if (error) {
    return <div className="p-6 text-red-600" data-id="element-65">{error}</div>;
  }
  const blendSubscription = subscriptions.find(sub => sub.id === 'blend');
  const pulseSubscription = subscriptions.find(sub => sub.id === 'pulse');
  return <div className="p-4 sm:p-6" data-id="element-66">
      <div className="flex justify-between items-center mb-4 sm:mb-6" data-id="element-67">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800" data-id="element-68">
          Billing
        </h1>
        <PaymentMethod data-id="element-69" />
      </div>
      <div className="space-y-6 sm:space-y-8" data-id="element-70">
        {blendSubscription && <div data-id="element-71">
            <div className="flex items-center gap-3 mb-4" data-id="element-72">
              <img src={blendSubscription.logoUrl} alt={blendSubscription.name} className="h-6 w-auto" data-id="element-73" />
              <StatusBadge status={blendSubscription.status} displayName={blendSubscription.statusDisplayName} data-id="element-74" />
              <div className="relative ml-auto" data-id="element-75">
                <button onClick={() => setBlendMenuOpen(!blendMenuOpen)} className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600" data-id="element-76">
                  {!['inactive', 'not_active', 'incomplete_payment', 'discounted', 'not_subscribed'].includes(blendSubscription.status) && <MoreVerticalIcon size={16} data-id="element-77" />}
                </button>
                {blendMenuOpen && <SubscriptionMenu status={blendSubscription.status} onCancel={() => handleCancelClick(blendSubscription)} onActivate={() => handleActivateClick(blendSubscription)} onClose={() => setBlendMenuOpen(false)} data-id="element-78" />}
              </div>
            </div>
            <div className="space-y-4" data-id="element-79">
              <ShopifySubscription subscription={blendSubscription} onUpgrade={() => handleUpgrade(blendSubscription)} data-id="element-80" />
              {blendSubscription.hasUsageFees && <StripeUsageFees status={blendSubscription.status} data-id="element-81" />}
            </div>
          </div>}
        {pulseSubscription && <div data-id="element-82">
            <div className="flex items-center gap-3 mb-4" data-id="element-83">
              <img src={pulseSubscription.logoUrl} alt={pulseSubscription.name} className="h-6 w-auto" data-id="element-84" />
              <StatusBadge status={pulseSubscription.status} displayName={pulseSubscription.statusDisplayName} data-id="element-85" />
              <div className="relative ml-auto" data-id="element-86">
                <button onClick={() => setPulseMenuOpen(!pulseMenuOpen)} className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600" data-id="element-87">
                  {!['inactive', 'not_active', 'incomplete_payment', 'discounted', 'not_subscribed'].includes(pulseSubscription.status) && <MoreVerticalIcon size={16} data-id="element-88" />}
                </button>
                {pulseMenuOpen && <SubscriptionMenu status={pulseSubscription.status} onCancel={() => handleCancelClick(pulseSubscription)} onActivate={() => handleActivateClick(pulseSubscription)} onClose={() => setPulseMenuOpen(false)} data-id="element-89" />}
              </div>
            </div>
            <PulseSubscription subscription={pulseSubscription} onUpgrade={() => handleUpgrade(pulseSubscription)} data-id="element-90" />
          </div>}
      </div>
      {showCancelModal && selectedSubscription && <CancellationModal onClose={() => {
      setShowCancelModal(false);
      setSelectedSubscription(null);
    }} onConfirm={handleCancelConfirm} subscriptionName={selectedSubscription.name} data-id="element-91" />}
      {showActivateModal && selectedSubscription && <ActivationModal onClose={() => {
      setShowActivateModal(false);
      setSelectedSubscription(null);
    }} onConfirm={handleActivateConfirm} subscriptionName={selectedSubscription.name} data-id="element-92" />}
    </div>;
};